/**
 * Configuración de Asistencia al Viaje: sobre QUIÉNES corre el proceso, qué
 * se le registra a cada grupo, y CUÁNDO corre solo.
 *
 * ## Los grupos
 *
 * La pantalla muestra SOLO los que están en el proceso. Los que no están se
 * agregan desde el diálogo, que los trae de GeoVictoria en ese momento
 * (09-10-2026). Listar los que están afuera no servía: la cuenta tiene
 * decenas y mezclarlos obligaba a buscar los que importan entre los que no.
 *
 * Cada grupo dice de qué operación es, porque de eso depende a qué base de
 * Cristal se le consultan los viajes. Sin operación no se puede guardar: un
 * grupo sin viajes consultables daría jornadas SIN TAREA por error, y esa
 * licencia no se puede dar de baja.
 *
 * "No marcar salida" es una decisión aparte: el grupo se procesa y se le
 * registra la licencia, pero no la salida al fin de turno. Es el caso de
 * CENCO desde el 06-10.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Loader2, Plus, Save, Trash2 } from 'lucide-react';

import { mensajeDe } from '../../api';
import { Aviso } from '../../components/Aviso';
import { useSesion } from '../../components/useSesion';
import { Programacion } from '../asistencia/Programacion';
import {
  guardarGrupos,
  leerGrupos,
  type AlcanceGruposDto,
  type GrupoAlcanceDto,
} from '../asistencia/api';
import { DialogoAgregarGrupos } from './DialogoAgregarGrupos';

export function PanelAsistencia(): React.JSX.Element {
  const [datos, setDatos] = useState<AlcanceGruposDto | null>(null);
  const [edicion, setEdicion] = useState<readonly GrupoAlcanceDto[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [agregando, setAgregando] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  const { sesion, modulo } = useSesion();
  const esAdministrador = modulo?.sinLogin === true || sesion?.rol === 'admin';

  const cargar = useCallback(async (): Promise<void> => {
    setCargando(true);
    try {
      const d = await leerGrupos();
      setDatos(d);
      setEdicion(d.grupos);
      setError('');
    } catch (err) {
      setDatos(null);
      setError(mensajeDe(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const ordenados = useMemo(
    () => [...edicion].sort((a, b) => a.grupo.localeCompare(b.grupo, 'es')),
    [edicion],
  );

  const hayCambios = useMemo(() => {
    const antes = datos?.grupos ?? [];
    if (antes.length !== edicion.length) return true;
    const previo = new Map(antes.map((g) => [g.grupo, g]));
    return edicion.some((g) => {
      const p = previo.get(g.grupo);
      return p === undefined || p.tms !== g.tms || p.sinSalidaArtificial !== g.sinSalidaArtificial;
    });
  }, [edicion, datos]);

  /** Agregados sin operación: no se puede guardar así. */
  const sinOperacion = ordenados.filter((g) => g.tms === '');

  const cambiar = (grupo: string, parcial: Partial<GrupoAlcanceDto>): void => {
    setAviso('');
    setEdicion((previos) => previos.map((g) => (g.grupo === grupo ? { ...g, ...parcial } : g)));
  };

  const quitar = (grupo: string): void => {
    setAviso('');
    setEdicion((previos) => previos.filter((g) => g.grupo !== grupo));
  };

  const agregar = (grupos: readonly string[]): void => {
    setAviso('');
    setEdicion((previos) => [
      ...previos,
      ...grupos.map((grupo) => ({
        grupo,
        // Si el despliegue tiene una sola operación se elige sola: pedirla
        // cuando no hay nada que elegir es un clic de más.
        tms: (datos?.operaciones.length === 1 ? datos.operaciones[0] : '') ?? '',
        sinSalidaArtificial: false,
      })),
    ]);
  };

  const guardar = async (): Promise<void> => {
    setGuardando(true);
    try {
      const d = await guardarGrupos(edicion);
      setDatos(d);
      setEdicion(d.grupos);
      setError('');
      setAviso('Grupos guardados. La próxima consulta ya los tiene en cuenta.');
    } catch (err) {
      setAviso('');
      setError(mensajeDe(err));
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <p className="nota">
        <Loader2 className="girando" size={16} aria-hidden="true" /> Cargando los grupos…
      </p>
    );
  }

  return (
    <>
      <section className="panel-alcance">
        <h2 className="titulo-bloque">Grupos en el proceso</h2>
        {error !== '' && <Aviso tono="error">{error}</Aviso>}
        {aviso !== '' && <Aviso tono="ok">{aviso}</Aviso>}

        <p className="nota">
          Las entradas de estos grupos son las que el proceso evalúa. Cada uno indica de qué
          operación es, que decide a qué TMS se le consultan los viajes.
        </p>

        {!esAdministrador && (
          <Aviso tono="atencion">
            Solo un Administrador puede cambiar esto. Podés ver cómo está configurado.
          </Aviso>
        )}

        {sinOperacion.length > 0 && (
          <Aviso tono="atencion">
            {`Falta la operación de: ${sinOperacion
              .map((g) => g.grupo)
              .join(', ')}. Sin eso no se sabe a qué TMS consultarle los viajes y sus jornadas saldrían Sin tarea por error.`}
          </Aviso>
        )}

        {ordenados.length === 0 && (
          <Aviso tono="atencion">
            No hay ningún grupo en el proceso: así no tiene a quién procesar. Para frenarlo del
            todo conviene desactivar la ejecución automática, no vaciar la lista.
          </Aviso>
        )}

        <fieldset className="grupos-alcance grupos-alcance--lista" disabled={!esAdministrador}>
          <legend>{`${String(ordenados.length)} grupo${ordenados.length === 1 ? '' : 's'}`}</legend>

          {ordenados.length === 0 ? (
            <p className="nota">Todavía no hay ninguno. Agregá el primero.</p>
          ) : (
            ordenados.map((g) => (
              <div key={g.grupo} className="grupo-alcance">
                <span className="grupo-alcance__nombre">{g.grupo}</span>
                <span className="grupo-alcance__opciones">
                  <select
                    aria-label={`Operación de ${g.grupo}`}
                    className={g.tms === '' ? 'invalida' : undefined}
                    value={g.tms}
                    onChange={(ev) => cambiar(g.grupo, { tms: ev.target.value })}
                  >
                    <option value="">— operación —</option>
                    {(datos?.operaciones ?? []).map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                  <label>
                    <input
                      type="checkbox"
                      checked={g.sinSalidaArtificial}
                      onChange={(ev) =>
                        cambiar(g.grupo, { sinSalidaArtificial: ev.target.checked })
                      }
                    />
                    No marcar salida
                  </label>
                  <button
                    type="button"
                    className="plano"
                    aria-label={`Quitar ${g.grupo} del proceso`}
                    onClick={() => quitar(g.grupo)}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </button>
                </span>
              </div>
            ))
          )}
        </fieldset>

        {/*
          El botón va FUERA de la lista: adentro queda debajo del scroll y con
          dieciséis grupos no se ve nunca. Fantasma, para que no compita con
          Guardar.
        */}
        <button
          type="button"
          className="agregar-grupos"
          disabled={!esAdministrador}
          onClick={() => setAgregando(true)}
        >
          <Plus size={15} aria-hidden="true" />
          Agregar grupos
        </button>

        <div className="acciones">
          <button
            type="button"
            disabled={
              !esAdministrador ||
              guardando ||
              !hayCambios ||
              ordenados.length === 0 ||
              sinOperacion.length > 0
            }
            onClick={() => void guardar()}
          >
            {guardando ? (
              <Loader2 className="girando" size={16} aria-hidden="true" />
            ) : (
              <Save size={16} aria-hidden="true" />
            )}
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          {hayCambios && <span className="nota">Hay cambios sin guardar</span>}
        </div>
      </section>

      <DialogoAgregarGrupos
        abierto={agregando}
        onCerrar={() => setAgregando(false)}
        onAgregar={agregar}
      />

      {/*
        Cuándo corre solo. Antes era una sub-solapa de la consola de jornadas;
        se movió acá porque es configuración, no operación diaria (pedido del
        26-09). El componente es el mismo: solo cambió de lugar.
      */}
      {esAdministrador && (
        <section className="panel-alcance">
          <h2 className="titulo-bloque">Ejecución automática</h2>
          <Programacion />
        </section>
      )}
    </>
  );
}
